import styled from '@emotion/styled';
import { Label, PageHeader, UpperInfoContainer } from 'components/PageLayout';
import { useParams } from 'react-router-dom';

import { BackButton } from 'components/Button';
import { Container, GridItem } from 'components/Layout';
import { Heading } from 'components/Typography';

import PrimaryToast from 'components/PrimaryToast';
import { useState } from 'react';
import { FeedbackReport } from './FeedbackReport';
import { ActiveWebUsageV2 } from './ActiveWebUsageReport';
import { AllSitesConnectionReport } from './AllSitesConnectionReport';
import { B2BRestMessagesReport } from './B2BRestMessagesReport';
import { CombinedNpsSmsEmailBillingReport } from './CombinedNpsSmsEmailBillingReport';
import { CoreMIReport } from './CoreMIReport';
import { CoveaReport } from './CoveaReport';
import { ZurichCSIReport } from './ZurichCSIReport';
import { DetailedCycleTimeReport } from './DetailedCycleTimeReport';
import { EmailReport } from './EmailReport';
import { InvoiceExtract } from './InvoiceExtractReport';
import { LVOWNKey2KeyReport } from './LVOWNKey2KeyReport';
import { MonthlyNotificationVolume } from './MonthlyNotificationVolumeReport';
import { NargControlSheetV5 } from './NARGControlReport';
import { NPSDetailV4 } from './NPSDetailV4Report';
import { NPSSummaryReport } from './NPSSummaryReport';
import { Rolling30DayReport } from './Rolling30DayReport';
import { RSAKey2KeyReport } from './RSAKey2KeyReport';
import { WPSiteConnectionsReport } from './WPSiteConnectionsReport';
import { SMSReport } from './SMSReport';
import { TotalLoss } from './TotalLoss';
import { VehiclesOnSite } from './VehiclesOnSite';
import { VolumeTrendsReport } from './VolumeTrendsReport';
import { WorkInProgress } from './WorkInProgressReport';
import { SLAPerformanceV3 } from './SLAPerformanceReport';


type ReportViewerParams = {
  reportPath: string;
};

function ReportNotFound() {
  return (
    <MainContainer direction="column">
      <Container direction="column">
        <GridItem>
          <Container>
            <BackButton text="Back to Reports" />
          </Container>
        </GridItem>
        <PageHeader>
          <Heading>Not found</Heading>
        </PageHeader>
        <GridItem>
          <Label>No report found, please try again.</Label>
        </GridItem>
      </Container>
    </MainContainer>
  );
}

export function ReportsDetailsMigrated() {
  const { reportPath } = useParams<ReportViewerParams>();
  const CurrentReport = generateReportTitle(reportPath || '');
  const ReportComponent = CurrentReport.component;
  const [message, setMessage] = useState<string>('');
  const [isOpenToast, setIsOpenToast] = useState<boolean>(false);
  return (
    <>
      <ReportComponent
        reportPath={reportPath || ''}
        setErrorMessage={(data: string) => setMessage(data)}
        setToast={(data: boolean) => setIsOpenToast(data)}
      />
      <PrimaryToast
        message={message}
        isOpen={isOpenToast}
        onClose={() => setIsOpenToast(false)}
        severity="error"
      />
    </>
  );

  function generateReportTitle(report: string) {
    switch (report) {

      case 'GetFeedbackReport': {
        return {
          title: 'Feedback Report',
          component: FeedbackReport,
        };
      }
      case 'ActiveWebUsage01': {
        return {
          title: 'Activeweb Usage Report V2',
          component: ActiveWebUsageV2,
        };
      }
      case 'GetAllSiteConnections': {
        return {
          title: 'All Sites Connections',
          component: AllSitesConnectionReport,
        };
      }
      case 'GetB2BRestQueueMessages': {
        return {
          title: 'B2B Rest Queue Messages',
          component: B2BRestMessagesReport,
        };
      }
      case 'GetCombinedBillingReport': {
        return {
          title: 'Combined NPS, SMS and Email Billing Report',
          component: CombinedNpsSmsEmailBillingReport,
        }
      }
      case 'GetCoreMIReport': {
        return {
          title: 'Core MI Report',
          component: CoreMIReport,
        };
      }
      case 'GetRepairStatusReportCovea': {
        return { title: 'Repair Status Portal Usage', component: CoveaReport };
      }
      case 'GetCSITriggerReport': {
        return {
          title: 'CSI Trigger Report',
          component: ZurichCSIReport,
        };
      }
      case 'GetDetailedCycleTimeReportExV2': {
        return {
          title: 'Detailed Cycle Time Report with Exceptions',
          component: DetailedCycleTimeReport,
        };
      }
        case 'GetEmailReport': {
          return {
            title: 'Email Report',
            component: EmailReport,
          }
      }
      case 'GetInvoiceExtractReport': {
        return { title: 'Invoice Extract Report', component: InvoiceExtract };
      }
      case 'GetKey2KeyDataForLVOWN': {
        return {
          title: 'LVOWN Key2Key Report',
          component: LVOWNKey2KeyReport,
        };
      }
      case 'GetMonthlyNotificationVolume': {
        return {
          title: 'Monthly Notification Volume Report',
          component: MonthlyNotificationVolume,
        }
      }
      case 'GetNARGControlSheetV05': {
        return {
          title: 'Narg Control Sheet V5',
          component: NargControlSheetV5,
        };
      }
      case 'GetNPSDetailReportV04': {
        return {
          title: 'NPS Detail Report V4',
          component: NPSDetailV4,
        };
      }
      case 'GetNPSSummaryReportV04': {
        return {
          title: 'NPS Summary Report V4',
          component: NPSSummaryReport,
        };
      }
      case 'Rolling30DayReport': {
        return {
          title: 'Repairer Capacity Report',
          component: Rolling30DayReport,
        };
      }
      case 'GetKey2KeyDataForRSA': {
        return {
          title: 'RSA Key2Key Report',
          component: RSAKey2KeyReport,
        };
      }      
      case 'GetSLAPerformanceV3Report': {
        return {
          title: 'SLA Performance V3 Report',
          component: SLAPerformanceV3,
        };
      }
      case 'GetSMSReport': {
        return { title: 'SMS Report', component: SMSReport };
      }
      case 'GetTLEngineeringSchedule': {
        return {
          title: 'TL Engineering Schedule Report',
          component: TotalLoss,
        };
      }
      case 'GetVehiclesOnSite': {
        return {
          title: 'Vehicles On Site',
          component: VehiclesOnSite,
        };
      }
      case 'GetVolumeTrendsReport': {
        return {
          title: 'Volume Trends Report',
          component: VolumeTrendsReport,
        }
      }
      case 'GetWorkInProgressV3Report': {
        return {
          title: 'Work In Progress V3 Report',
          component: WorkInProgress,
        };
      }
      case 'GetWPSiteConnections': {
        return {
          title: 'WP Site Connections Report',
          component: WPSiteConnectionsReport
        }
      }
      
      default: {
        return { title: 'Report not found', component: ReportNotFound };
      }
    }
  }
}

const MainContainer = styled(UpperInfoContainer)`
  padding: ${({ theme }) => `0 ${theme.padding.xl}`};
`;
